<script setup>
import { ref, onMounted, computed } from "vue";
import axios from 'axios';
import { useLoading } from 'vue-loading-overlay'
import Layout from "@/layouts/mainfw.vue";
import PageHeader from "@/components/page-header";
import { useFastWay } from "@/state/modules/fastway";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import { AgGridVue } from "ag-grid-vue3";
import Dialog from '@/views/components/Dialog.vue';
import {DropDownListComponent} from "@syncfusion/ej2-vue-dropdowns";
import { Modal, Toast } from 'bootstrap';
import * as XLSX from 'xlsx';
import * as mammoth from 'mammoth/mammoth.browser';
import DOMPurify from 'dompurify';

const store = useFastWay();
const loader = useLoading();
let dialog = ref(null);
let loaded = ref(false);
let projects = ref([]);
let data = ref([]);
let documentsModalRef = ref(null);
let documentsModalObj = null;
let closeDates = ref([]);
let qnasFiles = ref([]);
let qnasFullPath = ref('');
let qnasLoading = ref(false);
let qnasError = ref('');
let qnasUploading = ref(false);
let qnasDragActive = ref(false);
let fileInputRef = ref(null);
let previewModalRef = ref(null);
let previewModalObj = null;
let previewFile = ref(null);
let previewUrl = ref('');
let previewType = ref('');
let previewText = ref('');
let previewHtml = ref('');
let previewLoading = ref(false);
let previewError = ref('');
let contextMenu = ref({ visible: false, x: 0, y: 0, file: null });
let downloadingFile = ref('');
let dragCounter = ref(0);
let duplicateModalRef = ref(null);
let duplicateModalObj = null;
let pendingFiles = ref([]);
let pendingDuplicates = ref([]);
const title = "Ejecución de Indicadores";
const items = [
  {
    text: "Tablero",
    href: "/",
  },
  {
    text: "Ejecución de Indicadores",
    active: true,
  },
];
const months = ref([
{ id:  1, name: 'Enero' },
{ id:  2, name: 'Febrero' },
{ id:  3, name: 'Marzo' },
{ id:  4, name: 'Abril' },
{ id:  5, name: 'Mayo' },
{ id:  6, name: 'Junio' },
{ id:  7, name: 'Julio' },
{ id:  8, name: 'Agosto' },
{ id:  9, name: 'Septiembre' },
{ id: 10, name: 'Octubre' },
{ id: 11, name: 'Noviembre' },
{ id: 12, name: 'Diciembre' },
])

const api = computed({
    get() {
      return store.parameters.apiURL;
    }
});

const viewParms = computed({
  get() {
    return store.viewParameters.projectsMetricsExecution;
  }
});

const getTextColorForBackground = hexColor => {
  if (!hexColor || typeof hexColor !== 'string') return 'black';
  const hex = hexColor.replace('#', '');
  if (hex.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(hex)) return 'black';
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 128 ? 'black' : 'white';
}

const getCellStyle = params => {
  const bgColor = params.data.color || '#ffffff';
  const textColor = getTextColorForBackground(params.data.color);
  return {'backgroundColor': bgColor, 'color': textColor, 'textAlign': 'right'};
}

const formatZero = params => {
  if (typeof params.value !== 'undefined' && params.value !== null && params.value !== 0) {
    return params.value;
  }
  return '';
}

const formatFileSize = bytes => {
  if (!bytes || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = (bytes / Math.pow(1024, Math.min(i, units.length - 1))).toFixed(i === 0 ? 0 : 2);
  return `${size} ${units[Math.min(i, units.length - 1)]}`;
}

const getFileIcon = (fileName) => {
  const ext = fileName.split('.').pop().toLowerCase();
  const iconMap = {
    pdf: 'las la-file-pdf',
    doc: 'las la-file-word',
    docx: 'las la-file-word',
    xls: 'las la-file-excel',
    xlsx: 'las la-file-excel',
    csv: 'las la-file-excel',
    ppt: 'las la-file-powerpoint',
    pptx: 'las la-file-powerpoint',
    png: 'las la-file-image',
    jpg: 'las la-file-image',
    jpeg: 'las la-file-image',
    gif: 'las la-file-image',
    bmp: 'las la-file-image',
    svg: 'las la-file-image',
    webp: 'las la-file-image',
    txt: 'las la-file-alt',
    zip: 'las la-file-archive',
    rar: 'las la-file-archive',
    '7z': 'las la-file-archive',
    tar: 'las la-file-archive',
    gz: 'las la-file-archive',
    mp3: 'las la-file-audio',
    wav: 'las la-file-audio',
    ogg: 'las la-file-audio',
    mp4: 'las la-file-video',
    avi: 'las la-file-video',
    mov: 'las la-file-video',
    mkv: 'las la-file-video',
    js: 'las la-file-code',
    ts: 'las la-file-code',
    html: 'las la-file-code',
    css: 'las la-file-code',
    json: 'las la-file-code',
    xml: 'las la-file-code'
  };
  return iconMap[ext] || 'las la-file';
}

const formatDateTime = dateStr => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleString('es-GT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const totals = computed(() => {
  const summaryGroups = {};
  data.value.forEach(row => {
    const summary = row.summary || '';
    if (!summary) return;
    if (!summaryGroups[summary]) {
      summaryGroups[summary] = {
        totalGoal: 0,
        totalExecution: 0,
        participationPercentage: Number(row.participationPercentage) || 0
      };
    }
    summaryGroups[summary].totalGoal += Number(row.goal) || 0;
    summaryGroups[summary].totalExecution += Number(row.execution) || 0;
  });
  
  const totalGoal = Object.values(summaryGroups).reduce((sum, g) => sum + g.totalGoal, 0);
  const totalExecution = Object.values(summaryGroups).reduce((sum, g) => sum + g.totalExecution, 0);
  
  const percentage = Object.values(summaryGroups).reduce((sum, g) => {
    const ratio = g.totalGoal > 0 ? g.totalExecution / g.totalGoal : 1;
    return sum + ratio * g.participationPercentage;
  }, 0);
  
  return { totalGoal, totalExecution, percentage };
});

const isReadOnly = computed(() => {
  if (!closeDates.value || closeDates.value.length === 0) return false;
  // The API stores close dates as UTC midnight (e.g. 2026-05-31T00:00:00Z).
  // Compare calendar dates (local "today" vs. the close date's UTC day) instead of
  // comparing raw instants, otherwise the lock triggers hours early/late depending
  // on the user's timezone offset from UTC.
  const dates = closeDates.value
    .map(d => new Date(d.date))
    .filter(d => !isNaN(d.getTime()));
  if (dates.length === 0) return false;
  const latestDate = dates.reduce((latest, d) => d > latest ? d : latest, dates[0]);
  const closeDay = new Date(latestDate.getUTCFullYear(), latestDate.getUTCMonth(), latestDate.getUTCDate());
  const today = new Date();
  const localToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return closeDay < localToday;
});

const columnDefs = computed(() => {
  const p = totals.value.percentage.toFixed(2);
  let headerClass = 'header-red';
  
  if (p > 33 && p <= 66) {
    headerClass = 'header-yellow';
  } else if (p > 66) {
    headerClass = 'header-green';
  }

  return [
    {
      headerName: 'Área De Evaluación',
      field: 'activityDescription',
      autoHeight: true,
      width: 180,
      sortable: false,
      pinned: 'left',
      cellStyle: params => {
        const bgColor = params.data.color || '#ffffff';
        const textColor = getTextColorForBackground(params.data.color);
        return {'white-space': 'normal', 'backgroundColor': bgColor, 'color': textColor};
      }
    },
    {
      headerName: 'Indicador De Cambio Esperado',
      field: 'description',
      autoHeight: true,
      width: 400,
      sortable: false,
      pinned: 'left',
      cellStyle: params => {
        const bgColor = params.data.color || '#ffffff';
        const textColor = getTextColorForBackground(params.data.color);
        return {'white-space': 'normal', 'backgroundColor': bgColor, 'color': textColor};
      }
    },
    {
      headerName: `${p}% Ejecutado`,
      headerClass: headerClass,
      children: [
        { headerName: 'Meta', field: 'goal', width: 100, cellStyle: getCellStyle, valueFormatter: formatZero },
        { headerName: 'Ejecución', field: 'execution', width: 100, cellStyle: getCellStyle, editable: params => !params.node.rowPinned && !isReadOnly.value, cellEditor: 'agNumberCellEditor', valueFormatter: formatZero }
      ]
    },
    { headerName: 'Comentarios', field: 'comments', flex: 1, cellStyle: params => { const bgColor = params.data.color || '#ffffff'; const textColor = getTextColorForBackground(params.data.color); return {'backgroundColor': bgColor, 'color': textColor, 'textAlign': 'left'}; }, editable: !isReadOnly.value, cellEditor: 'agTextCellEditor' }
  ];
});

const pinnedBottomRowData = computed(() => [{
  activityDescription: 'Total',
  description: `Porcentaje de ejecución del proyecto: ${totals.value.percentage.toFixed(2)}%`,
  goal: totals.value.totalGoal,
  execution: totals.value.totalExecution,
  color: '#f2f2f2'
}]);

onMounted(async () => {
  const loading = loader.show({loader: 'bars', color: '#0000FF'}, {after: () => 'Cargando catálogos'});
  const params = {
    userId: store.userInfo.userId,
    token: store.userInfo.token
  };
  await axios
  .post(`${api.value}/sbg/projects/user_list`, params)
  .then(response => {
    if (response.data.success) {
      projects.value = response.data.data;
    } else {
      dialog.value.show('error', 'Error al cargar proyectos del usuario', response.data.message);
    }
    refreshData();
    loading.hide();
    loaded.value = true;
  })
  .catch(error => {
      let errorMessage = '';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response) {
        errorMessage = error.response.data.ErrorMessage;
      } else {
        errorMessage = 'Error de conectividad al cargar catálogo';
      }
      loading.hide();
      dialog.value.show('error', 'Error al cargar catálogos', errorMessage);
  });

  documentsModalObj = new Modal(documentsModalRef.value);
  previewModalObj = new Modal(previewModalRef.value);
  previewModalRef.value.addEventListener('hidden.bs.modal', closePreview);
  duplicateModalObj = new Modal(duplicateModalRef.value);
  window.addEventListener('keydown', onGlobalKeydown);
  window.addEventListener('scroll', closeContextMenu, true);
});

const getData = async () => {
  data.value = [];
  const loading = loader.show({loader: 'bars', color: '#0000FF'}, {after: () => 'Cargando métricas del proyecto'});
  const params = {
    userId: store.userInfo.userId,
    token: store.userInfo.token,
    projectId: viewParms.value.projectId,
    year: viewParms.value.year,
    month: viewParms.value.month,
    context: store.userInfo
  };
  await axios
  .post(`${api.value}/sbg/projects/metrics/month`, params)
  .then(response => {
    if (response.data.success) {
      if (response.data.data) {
        data.value = response.data.data;
      }
    } else {
      dialog.value.show('error', 'Error interno al cargar métricas del proyecto', response.data.message);
    }
    loading.hide();
    loaded.value = true;
  })
  .catch(error => {
    loaded.value = true;
    loading.hide();
    dialog.value.show('error', 'Error al cargar métricas del proyecto', error.message);
  });
}

const getCloseDates = async () => {
  const payload = {
    userId: store.userInfo.userId,
    login: store.userInfo.login || store.userInfo.userName || '',
    token: store.userInfo.token,
    dbName: store.userInfo.organizationName || '',
    year: viewParms.value.year,
    month: viewParms.value.month
  };
  try {
    const response = await axios.post(`${api.value}/sbg/projects/close_dates/get_by_month`, payload);
    if (response.data.success) {
      closeDates.value = Array.isArray(response.data.data) ? response.data.data : [];
    } else {
      closeDates.value = [];
      dialog.value.show('error', 'Error al obtener fechas de cierre', response.data.message || '');
    }
  } catch (error) {
    closeDates.value = [];
    let errorMessage = 'Error de conectividad';
    if (error.response && error.response.data && error.response.data.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    dialog.value.show('error', 'Error al obtener fechas de cierre', errorMessage);
  }
};

const refreshData = async () => {
  await Promise.all([getCloseDates(), getData()]);
};

const saveExecution = async (params) => {
  const executionValue = params.column.colDef.field === 'execution' ? params.newValue : params.data.execution;
  const commentsValue = params.column.colDef.field === 'comments' ? params.newValue : params.data.comments;

  const payload = {
    context: {
      userId: store.userInfo.userId,
      username: store.userInfo.username || '',
      role: store.userInfo.role || '',
      token: store.userInfo.token
    },
    projectId: viewParms.value.projectId,
    activityId: params.data.activityId,
    year: viewParms.value.year,
    month: viewParms.value.month,
    execution: executionValue,
    comments: commentsValue
  };
  
  try {
    const response = await axios.post(`${api.value}/sbg/projects/metrics/save_execution`, payload);
    if (response.data.success) {
      showToast('Dato grabado correctamente', 'success');
      getData();
    } else {
      dialog.value.show('error', 'Error al actualizar ejecución', response.data.message);
    }
  } catch (error) {
    let errorMessage = 'Error de conectividad';
    if (error.response && error.response.data && error.response.data.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    dialog.value.show('error', 'Error al actualizar ejecución', errorMessage);
  }
}

const showToast = (message, variant = 'success') => {
  const bgClass = variant === 'success' ? 'bg-success' : 'bg-danger';
  const container = document.getElementById('toast-container');
  if (!container) return;
  const toastHtml = `
    <div class="toast align-items-center ${bgClass} text-white border-0" role="alert" aria-live="assertive" aria-atomic="true">
      <div class="d-flex">
        <div class="toast-body">${message}</div>
        <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
      </div>
    </div>
  `;
  container.insertAdjacentHTML('beforeend', toastHtml);
  const toastElement = container.lastElementChild;
  const bsToast = new Toast(toastElement, { delay: 3000 });
  bsToast.show();
  toastElement.addEventListener('hidden.bs.toast', () => {
    toastElement.remove();
  });
}

const onCellValueChanged = (params) => {
  if (isReadOnly.value) return;
  if (params.column.colDef.field === 'execution' || params.column.colDef.field === 'comments') {
    saveExecution(params);
  }
}

const years = computed(() => {
  const currentYear = new Date().getFullYear() + 1;
  return Array.from({ length: 6 }, (_, i) => currentYear - i);
});

const onYearChange = () => {
  refreshData();
}

const documentsModalTitle = computed(() => {
  const monthName = months.value.find(m => m.id === viewParms.value.month)?.name || '';
  const year = viewParms.value.year || '';
  return `Documentos de ${monthName} de ${year}`;
});

const selectedProjectName = computed(() => {
  const project = projects.value.find(p => p.id === viewParms.value.projectId);
  return project?.name || '';
});

const showDocumentsModal = () => {
  qnasFiles.value = [];
  qnasFullPath.value = '';
  qnasError.value = '';
  documentsModalObj.show();
  loadQnasFiles();
};

const loadQnasFiles = async () => {
  if (!viewParms.value.projectId) {
    qnasError.value = 'Debe seleccionar un proyecto';
    return;
  }

  qnasError.value = '';

  if (qnasLoading.value) return;

  qnasLoading.value = true;

  const payload = {
    context: {
      userId: store.userInfo.userId,
      token: store.userInfo.token,
      projectId: viewParms.value.projectId,
      year: viewParms.value.year,
      month: viewParms.value.month
    }
  };

  try {
    const response = await axios.post(`${api.value}/sbg/projects/qnas/list_files`, payload);
    if (response.data.success) {
      qnasFiles.value = response.data.data?.files || [];
      qnasFullPath.value = response.data.data?.fullPath || '';
    } else {
      qnasError.value = response.data.message || 'Error al listar archivos';
    }
  } catch (error) {
    qnasError.value = error.response?.data?.message || error.message || 'Error de conectividad al listar archivos de QNAS';
  } finally {
    qnasLoading.value = false;
  }
};

const onDragOver = (event) => {
  event.preventDefault();
};

const onDragEnter = (event) => {
  event.preventDefault();
  if (isReadOnly.value) return;
  dragCounter.value++;
  qnasDragActive.value = true;
};

const onDragLeave = (event) => {
  event.preventDefault();
  dragCounter.value--;
  if (dragCounter.value <= 0) {
    dragCounter.value = 0;
    qnasDragActive.value = false;
  }
};

const onDrop = (event) => {
  event.preventDefault();
  if (isReadOnly.value) {
    dragCounter.value = 0;
    qnasDragActive.value = false;
    return;
  }
  dragCounter.value = 0;
  qnasDragActive.value = false;
  const files = event.dataTransfer.files;
  if (files.length > 0) {
    handleQnasFiles(Array.from(files));
  }
};

const onFileInputChange = (event) => {
  if (isReadOnly.value) {
    event.target.value = '';
    return;
  }
  const files = event.target.files;
  if (files.length > 0) {
    handleQnasFiles(Array.from(files));
  }
  event.target.value = '';
};

const handleQnasFiles = (files) => {
  if (isReadOnly.value) return;
  if (!files || files.length === 0) return;

  const duplicates = files.filter(file => {
    return qnasFiles.value.some(f => f.name === file.name && !f.isDirectory);
  });

  if (duplicates.length > 0) {
    pendingFiles.value = files;
    pendingDuplicates.value = duplicates;
    duplicateModalObj.show();
    return;
  }

  uploadQnasFiles(files);
};

const confirmOverwriteAll = () => {
  uploadQnasFiles(pendingFiles.value);
  closeDuplicateModal();
};

const confirmOnlyNewFiles = () => {
  const newFiles = pendingFiles.value.filter(f => !pendingDuplicates.value.includes(f));
  if (newFiles.length > 0) {
    uploadQnasFiles(newFiles);
  }
  closeDuplicateModal();
};

const cancelDuplicateUpload = () => {
  pendingFiles.value = [];
  pendingDuplicates.value = [];
  duplicateModalObj.hide();
};

const closeDuplicateModal = () => {
  pendingFiles.value = [];
  pendingDuplicates.value = [];
  duplicateModalObj.hide();
};

const uploadQnasFile = async (file) => {
  const formData = new FormData();
  formData.append('userId', store.userInfo.userId);
  formData.append('login', store.userInfo.login || store.userInfo.userName || '');
  formData.append('token', store.userInfo.token);
  formData.append('dbName', store.userInfo.organizationName || '');
  formData.append('projectId', viewParms.value.projectId);
  formData.append('year', viewParms.value.year);
  formData.append('month', viewParms.value.month);
  formData.append('file', file);

  const response = await axios.post(`${api.value}/sbg/projects/qnas/upload_file`, formData);
  return response;
};

const uploadQnasFiles = async (files) => {
  if (isReadOnly.value) return;
  if (!viewParms.value.projectId) {
    dialog.value.show('error', 'Error', 'Debe seleccionar un proyecto');
    return;
  }

  qnasUploading.value = true;

  let successCount = 0;
  let errorCount = 0;
  const errors = [];

  try {
    for (const file of files) {
      try {
        const response = await uploadQnasFile(file);
        if (response.data.success) {
          successCount++;
        } else {
          errorCount++;
          errors.push(`${file.name}: ${response.data.message || 'Error al subir'}`);
        }
      } catch (error) {
        errorCount++;
        const message = error.response?.data?.message || error.message || 'Error de conectividad';
        errors.push(`${file.name}: ${message}`);
      }
    }

    if (successCount > 0) {
      const message = files.length === 1
        ? `Archivo "${files[0].name}" subido correctamente a ${qnasFullPath.value}`
        : `${successCount} de ${files.length} archivo(s) subido(s) correctamente a ${qnasFullPath.value}`;
      showToast(message, 'success');
    }

    if (errorCount > 0) {
      showToast(`Fallaron ${errorCount} archivo(s): ${errors.join('; ')}`, 'danger');
    }
  } finally {
    qnasUploading.value = false;
    loadQnasFiles();
  }
};

const getFileExtension = (fileName) => {
  return fileName.split('.').pop().toLowerCase();
};

const OFFICE_PREVIEW_MAX_SIZE = 10 * 1024 * 1024; // 10MB

const escapeHtml = (value) => {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const getPreviewType = (fileName) => {
  const ext = getFileExtension(fileName);
  if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'].includes(ext)) return 'image';
  if (ext === 'pdf') return 'pdf';
  if (['txt', 'csv', 'json', 'xml', 'md', 'log'].includes(ext)) return 'text';
  if (['mp4', 'webm', 'ogg'].includes(ext)) return 'video';
  if (['mp3', 'wav', 'ogg'].includes(ext)) return 'audio';
  if (['xls', 'xlsx'].includes(ext)) return 'spreadsheet';
  if (ext === 'docx') return 'document';
  if (ext === 'doc') return 'legacy-doc';
  return 'generic';
};

const onFileDblClick = (file) => {
  if (file.isDirectory) return;
  openFilePreview(file);
};

const openFilePreview = async (file) => {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value);
    previewUrl.value = '';
  }
  previewFile.value = file;
  previewType.value = getPreviewType(file.name);
  previewText.value = '';
  previewHtml.value = '';
  previewError.value = '';
  previewLoading.value = true;
  previewModalObj.show();

  try {
    const blob = await fetchQnasFileBlob(file);

    if (previewType.value === 'text') {
      previewText.value = await blob.text();
      previewUrl.value = URL.createObjectURL(blob);
    } else if (previewType.value === 'spreadsheet') {
      previewUrl.value = URL.createObjectURL(blob);
      if (blob.size > OFFICE_PREVIEW_MAX_SIZE) {
        previewError.value = 'El archivo es demasiado grande para mostrar una vista previa. Descárguelo para visualizarlo.';
      } else {
        previewHtml.value = await buildSpreadsheetPreview(blob);
      }
    } else if (previewType.value === 'document') {
      previewUrl.value = URL.createObjectURL(blob);
      if (blob.size > OFFICE_PREVIEW_MAX_SIZE) {
        previewError.value = 'El archivo es demasiado grande para mostrar una vista previa. Descárguelo para visualizarlo.';
      } else {
        previewHtml.value = await buildDocumentPreview(blob);
      }
    } else {
      previewUrl.value = URL.createObjectURL(blob);
    }
  } catch (error) {
    previewError.value = error.message || 'Error al obtener el archivo';
  } finally {
    previewLoading.value = false;
  }
};

const buildSpreadsheetPreview = async (blob) => {
  const arrayBuffer = await blob.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  let html = '';
  let hasContent = false;
  workbook.SheetNames.forEach((sheetName) => {
    const sheet = workbook.Sheets[sheetName];
    if (!sheet || !sheet['!ref']) {
      // Empty sheet or a sheet with no cell data (e.g. chart-only sheet):
      // sheet_to_html/decode_range would throw on a missing '!ref', so skip it.
      return;
    }
    let sheetHtml;
    try {
      sheetHtml = XLSX.utils.sheet_to_html(sheet, { editable: false });
    } catch (e) {
      sheetHtml = '<p class="text-muted">No se pudo generar la vista previa de esta hoja.</p>';
    }
    html += `<div class="spreadsheet-sheet${hasContent ? ' mt-4' : ''}"><h6 class="spreadsheet-sheet-title">${escapeHtml(sheetName)}</h6>${sheetHtml}</div>`;
    hasContent = true;
  });
  if (!hasContent) {
    return `<div class="text-center text-muted p-4">El archivo no contiene datos para mostrar en la vista previa.</div>`;
  }
  return DOMPurify.sanitize(html, { ADD_ATTR: ['target'] });
};

const buildDocumentPreview = async (blob) => {
  const arrayBuffer = await blob.arrayBuffer();
  const result = await mammoth.convertToHtml({ arrayBuffer });
  return DOMPurify.sanitize(result.value, {
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i
  });
};

const closePreview = () => {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value);
    previewUrl.value = '';
  }
  previewFile.value = null;
  previewText.value = '';
  previewHtml.value = '';
  previewError.value = '';
  previewType.value = '';
};

const downloadPreviewFile = () => {
  if (!previewFile.value) return;

  let url = previewUrl.value;
  if (!url && previewType.value === 'text') {
    const blob = new Blob([previewText.value], { type: 'text/plain' });
    url = URL.createObjectURL(blob);
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }

  if (!url) return;

  const a = document.createElement('a');
  a.href = url;
  a.download = previewFile.value.name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const fetchQnasFileBlob = async (file) => {
  const payload = {
    context: {
      userId: store.userInfo.userId,
      token: store.userInfo.token,
      projectId: viewParms.value.projectId,
      year: viewParms.value.year,
      month: viewParms.value.month
    },
    fileName: file.name
  };

  try {
    const response = await axios.post(
      `${api.value}/sbg/projects/qnas/download_file`,
      payload,
      {
        responseType: 'blob',
        headers: {
          'Accept': '*/*',
          'Content-Type': 'application/json'
        }
      }
    );
    const contentType = response.headers['content-type'] || 'application/octet-stream';
    return new Blob([response.data], { type: contentType });
  } catch (error) {
    let message = error.message || 'Error de conectividad';
    if (error.response && error.response.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const parsed = JSON.parse(text);
        if (parsed.message) message = parsed.message;
      } catch (e) {
        // ignore parsing errors
      }
    } else if (error.response?.data?.message) {
      message = error.response.data.message;
    }
    throw new Error(message);
  }
};

const downloadQnasFile = async (file) => {
  if (!file || file.isDirectory) return;
  if (!viewParms.value.projectId) {
    dialog.value.show('error', 'Error', 'Debe seleccionar un proyecto');
    return;
  }

  if (downloadingFile.value) return;
  downloadingFile.value = file.name;

  try {
    const blob = await fetchQnasFileBlob(file);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    showToast(`Descargando "${file.name}"`, 'success');
  } catch (error) {
    showToast(error.message || 'Error de conectividad al descargar el archivo', 'danger');
  } finally {
    downloadingFile.value = '';
  }
};

const onFileContextMenu = (event, file) => {
  if (file.isDirectory) return;
  event.preventDefault();
  const menuWidth = 180;
  const menuHeight = 90;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let x = event.clientX;
  let y = event.clientY;
  if (x + menuWidth > viewportWidth) x = viewportWidth - menuWidth - 5;
  if (y + menuHeight > viewportHeight) y = viewportHeight - menuHeight - 5;

  contextMenu.value = { visible: true, x, y, file };
};

const closeContextMenu = () => {
  contextMenu.value.visible = false;
  contextMenu.value.file = null;
};

const onContextMenuOpen = () => {
  if (contextMenu.value.file) {
    openFilePreview(contextMenu.value.file);
  }
  closeContextMenu();
};

const onContextMenuDownload = () => {
  const file = contextMenu.value.file;
  closeContextMenu();
  if (file) {
    downloadQnasFile(file);
  }
};

const onGlobalKeydown = (event) => {
  if (event.key === 'Escape' && contextMenu.value.visible) {
    closeContextMenu();
  }
};

</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
     <div class="row">
        <div class="col-lg-12">
            <div class="card">
                <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center align-middle row mb-2">
                        <div class="d-inline-block mr-1" style="width: 300px;">
                          <DropDownListComponent v-model:value="viewParms.projectId" floatLabelType="Auto" placeholder="Proyecto" highlight=true :dataSource="projects" :fields="{text: 'name', value: 'id'}" :showClearButton='true' :allowFiltering="true" :filterBarPlaceholder="'Buscar proyecto'" @change="refreshData"/>
                        </div>
                        <div class="col-1">
                          <DropDownListComponent v-model:value="viewParms.year" floatLabelType="Auto" placeholder="Año" :dataSource="years" :change="onYearChange"/>
                        </div>
                        <div class="col-1">
                          <DropDownListComponent
                            v-model:value="viewParms.month"
                            floatLabelType="Auto"
                            placeholder="Mes"
                            highlight=true
                            :dataSource="months"
                            :fields="{text: 'name', value: 'id'}"
                            @change="refreshData"
                          />
                        </div>
                        <div class="col-1 d-flex justify-content-start">
                          <b-button variant="outline-success" class="btn-icon waves-effect waves-light me-1" v-b-tooltip.hover title="Recargar" @click="refreshData">
                              <i class="las la-sync"></i>
                          </b-button>
                          <b-button variant="primary" class="btn-icon waves-effect waves-light" v-b-tooltip.hover title="Ver documentos" @click="showDocumentsModal">
                              <i class="las la-file-alt"></i>
                          </b-button>
                        </div>
                        <div class="col">
                        </div>
                    </div>
                    <ag-grid-vue
                        style="width: 100%; height: 700px;"
                        class="ag-theme-balham"
                        :columnDefs="columnDefs"
                        :rowData="data"
                        :pinnedBottomRowData="pinnedBottomRowData"
                        :accentedSort="true"
                        @cell-value-changed="onCellValueChanged"
                    >
                    </ag-grid-vue>
          </div>
          <div class="modal-footer bg-light d-flex justify-content-between align-items-center py-3">
            <small v-if="previewFile" class="text-muted">
              <i class="las la-info-circle me-1"></i>
              {{ previewFile.isDirectory ? 'Carpeta' : getFileExtension(previewFile.name).toUpperCase() }}
            </small>
            <div>
              <button
                v-if="previewFile && !previewLoading"
                type="button"
                class="btn btn-primary"
                @click="downloadPreviewFile"
              >
                <i class="las la-download me-1"></i> Descargar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Dialog ref="dialog"/>
    <div
      class="modal fade"
      ref="documentsModalRef"
      tabindex="-1"
      aria-labelledby="documentsModalLabel"
      aria-hidden="true"
      style="display: none"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header bg-light align-items-center py-3">
            <div>
              <h4 class="modal-title mb-1" id="documentsModalLabel">{{ documentsModalTitle }}</h4>
              <small class="text-muted">{{ selectedProjectName }}</small>
            </div>
            <div class="d-flex align-items-center">
              <button
                type="button"
                class="btn btn-outline-success btn-icon waves-effect waves-light me-2"
                title="Recargar listado"
                :disabled="qnasLoading || qnasUploading"
                @click="loadQnasFiles"
              >
                <i class="las la-sync"></i>
              </button>
              <button
                type="button"
                class="btn-close mt-0"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
          </div>
          <div class="modal-body documents-modal-body p-0" style="min-height: 400px;">
            <input type="file" ref="fileInputRef" multiple style="position: absolute; width: 1px; height: 1px; opacity: 0; overflow: hidden; pointer-events: none;" @change="onFileInputChange">
            <div v-if="qnasLoading" class="d-flex justify-content-center align-items-center" style="min-height: 400px;">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Cargando...</span>
              </div>
            </div>

            <div v-else-if="qnasError" class="d-flex justify-content-center align-items-center" style="min-height: 400px;">
              <div class="text-center text-danger">
                <i class="las la-exclamation-triangle" style="font-size: 3rem;"></i>
                <p class="mt-2">{{ qnasError }}</p>
              </div>
            </div>

            <div
              v-else
              class="documents-drop-area"
              :class="{ 'drag-active': qnasDragActive, 'uploading': qnasUploading }"
              @dragover.prevent="onDragOver"
              @dragenter.prevent="onDragEnter"
              @dragleave.prevent="onDragLeave"
              @drop.prevent="onDrop"
            >
              <div v-if="qnasUploading" class="documents-drop-overlay">
                <div class="text-center">
                  <div class="spinner-border text-primary mb-2" role="status">
                    <span class="visually-hidden">Subiendo...</span>
                  </div>
                  <p class="mb-0">Subiendo archivo(s)...</p>
                </div>
              </div>

              <div v-else-if="qnasDragActive" class="documents-drop-overlay">
                <div class="text-center">
                  <i class="las la-cloud-upload-alt display-3 text-primary"></i>
                  <p class="mb-0 mt-2 fw-semibold">Suelte los archivos aquí</p>
                </div>
              </div>

              <div v-if="qnasFiles.length === 0 && !qnasDragActive && !qnasUploading" class="d-flex flex-column justify-content-center align-items-center documents-drop-content text-center text-muted">
                <i class="las la-cloud-upload-alt display-4 mb-2"></i>
                <p class="mb-2">Arrastre y suelte archivos aquí</p>
                <small>No se encontraron archivos en este directorio</small>
              </div>

              <div v-if="qnasFiles.length > 0" class="documents-files-content">
                <div class="files-mosaic">
                  <div
                    v-for="(file, index) in qnasFiles"
                    :key="`${file.name}-${file.modTime}-${index}`"
                    class="file-tile"
                    :class="{ 'is-folder': file.isDirectory, 'is-previewable': !file.isDirectory }"
                    :title="file.isDirectory ? file.name : `${file.name} (doble clic o clic derecho para acciones)`"
                    @dblclick="onFileDblClick(file)"
                    @contextmenu="onFileContextMenu($event, file)"
                    @dragstart.prevent
                  >
                    <div class="file-tile-icon">
                      <i :class="file.isDirectory ? 'las la-folder' : getFileIcon(file.name)" style="font-size: 2.5rem;"></i>
                    </div>
                    <div class="file-tile-name" :class="{ 'fw-bold': file.isDirectory }">
                      {{ file.name }}
                    </div>
                    <div class="file-tile-meta">
                      <small v-if="!file.isDirectory" class="text-muted">{{ formatFileSize(file.size) }}</small>
                      <small v-else class="text-muted">Carpeta</small>
                    </div>
                    <div class="file-tile-date">
                      <small class="text-muted">{{ formatDateTime(file.modTime) }}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer bg-light d-flex justify-content-between align-items-center py-3">
            <button
              type="button"
              class="btn btn-outline-primary"
              :disabled="qnasUploading || isReadOnly"
              :title="isReadOnly ? 'El mes está cerrado, no se pueden cargar archivos' : ''"
              @click="fileInputRef.click()"
            >
              <i class="las la-upload me-1"></i> Cargar archivos
            </button>
            <button type="button" class="btn btn-primary" data-bs-dismiss="modal">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      class="modal fade"
      ref="previewModalRef"
      tabindex="-1"
      aria-labelledby="previewModalLabel"
      aria-hidden="true"
      style="display: none"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div class="modal-dialog modal-dialog-centered modal-xl">
        <div class="modal-content">
          <div class="modal-header bg-light align-items-center py-3">
            <div>
              <h4 class="modal-title mb-1" id="previewModalLabel">
                <i :class="previewFile ? getFileIcon(previewFile.name) : 'las la-file'" class="me-2"></i>
                {{ previewFile?.name || 'Vista previa' }}
              </h4>
              <small v-if="previewFile" class="text-muted">
                {{ formatFileSize(previewFile.size) }} · {{ formatDateTime(previewFile.modTime) }}
              </small>
            </div>
            <div class="d-flex align-items-center">
              <button
                v-if="previewFile && !previewLoading"
                type="button"
                class="btn btn-outline-primary btn-icon waves-effect waves-light me-2"
                title="Descargar"
                @click="downloadPreviewFile"
              >
                <i class="las la-download"></i>
              </button>
              <button
                type="button"
                class="btn-close mt-0"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
          </div>
          <div class="modal-body p-0" style="min-height: 500px; max-height: 80vh; overflow: auto; background-color: #f8f9fa;">
            <div v-if="previewLoading" class="d-flex justify-content-center align-items-center" style="min-height: 500px;">
              <div class="text-center">
                <div class="spinner-border text-primary mb-2" role="status">
                  <span class="visually-hidden">Cargando...</span>
                </div>
                <p class="mb-0 text-muted">Cargando vista previa...</p>
              </div>
            </div>

            <div v-else-if="previewError" class="d-flex justify-content-center align-items-center" style="min-height: 500px;">
              <div class="text-center text-danger">
                <i class="las la-exclamation-triangle" style="font-size: 3rem;"></i>
                <p class="mt-2">{{ previewError }}</p>
              </div>
            </div>

            <div v-else-if="previewType === 'image'" class="d-flex justify-content-center align-items-center p-3" style="min-height: 500px;">
              <img :src="previewUrl" :alt="previewFile?.name" class="img-fluid" style="max-height: 75vh;" />
            </div>

            <iframe
              v-else-if="previewType === 'pdf'"
              :src="previewUrl"
              style="width: 100%; height: 75vh; border: none;"
              :title="previewFile?.name"
            ></iframe>

            <video
              v-else-if="previewType === 'video'"
              :src="previewUrl"
              controls
              style="width: 100%; max-height: 75vh; display: block;"
            ></video>

            <audio
              v-else-if="previewType === 'audio'"
              :src="previewUrl"
              controls
              class="w-100 mt-3"
            ></audio>

            <pre
              v-else-if="previewType === 'text'"
              class="m-0 p-3"
              style="white-space: pre-wrap; word-break: break-word; font-family: monospace; font-size: 0.85rem;"
            ><code>{{ previewText }}</code></pre>

            <div
              v-else-if="previewType === 'spreadsheet'"
              class="spreadsheet-preview p-3"
              v-html="previewHtml"
            ></div>

            <div
              v-else-if="previewType === 'document'"
              class="document-preview p-4"
              v-html="previewHtml"
            ></div>

            <div v-else-if="previewType === 'legacy-doc'" class="d-flex justify-content-center align-items-center" style="min-height: 500px;">
              <div class="text-center text-muted">
                <i class="las la-file-word" style="font-size: 5rem;"></i>
                <p class="mt-3">La vista previa no está disponible para archivos .doc (formato antiguo de Word).<br>Descargue el archivo para visualizarlo.</p>
                <button type="button" class="btn btn-primary" @click="downloadPreviewFile">
                  <i class="las la-download me-1"></i> Descargar archivo
                </button>
              </div>
            </div>

            <div v-else class="d-flex justify-content-center align-items-center" style="min-height: 500px;">
              <div class="text-center text-muted">
                <i :class="previewFile ? getFileIcon(previewFile.name) : 'las la-file'" style="font-size: 5rem;"></i>
                <p class="mt-3">No hay vista previa disponible para este tipo de archivo</p>
                <button type="button" class="btn btn-primary" @click="downloadPreviewFile">
                  <i class="las la-download me-1"></i> Descargar archivo
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div id="toast-container" class="toast-container position-fixed bottom-0 end-0 p-3" style="z-index: 9999;"></div>
    <div
      v-if="contextMenu.visible"
      class="context-menu-backdrop"
      @click="closeContextMenu"
      @contextmenu.prevent="closeContextMenu"
    ></div>
    <ul
      v-if="contextMenu.visible && contextMenu.file"
      class="file-context-menu list-unstyled"
      :style="{ top: contextMenu.y + 'px', left: contextMenu.x + 'px' }"
    >
      <li class="file-context-menu-header">
        <i :class="getFileIcon(contextMenu.file.name)" class="me-2"></i>
        <span class="text-truncate" :title="contextMenu.file.name">{{ contextMenu.file.name }}</span>
      </li>
      <li class="file-context-menu-item" @click="onContextMenuOpen">
        <i class="las la-eye me-2"></i> Abrir
      </li>
      <li class="file-context-menu-item" :class="{ 'disabled': !!downloadingFile }" @click="onContextMenuDownload">
        <i class="las la-download me-2"></i>
        <span>Descargar</span>
        <i v-if="downloadingFile === contextMenu.file.name" class="las la-spinner la-spin ms-2"></i>
      </li>
    </ul>
    <div
      class="modal fade"
      ref="duplicateModalRef"
      tabindex="-1"
      aria-labelledby="duplicateModalLabel"
      aria-hidden="true"
      style="display: none"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-light">
            <h5 class="modal-title" id="duplicateModalLabel">
              <i class="las la-exclamation-triangle text-warning me-2"></i>
              Archivos duplicados
            </h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              @click="cancelDuplicateUpload"
            ></button>
          </div>
          <div class="modal-body">
            <p>Los siguientes archivos ya existen en este directorio:</p>
            <ul class="list-group list-group-flush mb-3">
              <li
                v-for="(file, index) in pendingDuplicates"
                :key="`dup-${file.name}-${index}`"
                class="list-group-item d-flex align-items-center px-0"
              >
                <i :class="getFileIcon(file.name)" class="me-2"></i>
                <span class="text-truncate">{{ file.name }}</span>
              </li>
            </ul>
            <p class="mb-0">¿Qué desea hacer?</p>
          </div>
          <div class="modal-footer bg-light">
            <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal" @click="cancelDuplicateUpload">
              Cancelar
            </button>
            <button type="button" class="btn btn-outline-primary" @click="confirmOnlyNewFiles">
              Solo los nuevos
            </button>
            <button type="button" class="btn btn-primary" @click="confirmOverwriteAll">
              Sobrescribir todo
            </button>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
@import "../../../../node_modules/@syncfusion/ej2-base/styles/material.css";
@import "../../../../node_modules/@syncfusion/ej2-vue-inputs/styles/material.css";
@import "../../../../node_modules/@syncfusion/ej2-vue-dropdowns/styles/material.css";

#input-container .e-input-group {
  margin: 30px 0;
}

.e-input-group-icon:before {
  font-family: e-icons;
}

.e-input-group .e-input-group-icon.e-input-clear .e-input-group-icon.e-input-search {
  font-size: 12px;
}

.e-input-group.e-small .e-input-group-icon.e-input-clear .e-input-group-icon.e-input-search {
  font-size: 12px;
}

.e-input-group-icon.e-input-clear:before {
  content: "\e7a7";
}

.e-input-group-icon.e-input-search:before {
  content: "\e993";
}

.number-cell {
  text-align: right;
}

:deep(.ag-theme-balham .ag-input-field-input) {
  color: black !important;
  background-color: white !important;
}

:deep(.header-red),
:deep(.header-yellow),
:deep(.header-green) {
  font-weight: bold;
  font-size: 1.1em;
}

:deep(.header-red .ag-header-group-cell-label),
:deep(.header-yellow .ag-header-group-cell-label),
:deep(.header-green .ag-header-group-cell-label) {
  justify-content: center !important;
  width: 100%;
}

:deep(.header-red .ag-header-group-cell-text),
:deep(.header-yellow .ag-header-group-cell-text),
:deep(.header-green .ag-header-group-cell-text) {
  text-align: center !important;
}

:deep(.header-red) {
  background-color: #dc3545;
  color: #ffffff;
}

:deep(.header-yellow) {
  background-color: #ffc107;
  color: #000000;
}

:deep(.header-green) {
  background-color: #198754;
  color: #ffffff;
}

.documents-modal-body {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 400px;
  max-height: calc(100vh - 180px);
  overflow: hidden;
}

.documents-drop-area {
  position: relative;
  flex: 1;
  border: 2px dashed transparent;
  border-radius: 0;
  transition: all 0.2s ease-in-out;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.documents-drop-area.drag-active {
  border-color: #0d6efd;
  background-color: rgba(13, 110, 253, 0.08);
}

.documents-drop-area.uploading {
  cursor: default;
  background-color: rgba(13, 110, 253, 0.04);
}

.documents-drop-content {
  flex: 1;
  min-height: 300px;
  padding: 1rem;
}

.documents-drop-overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(13, 110, 253, 0.08);
  border: 2px dashed #0d6efd;
  pointer-events: none;
}

.documents-files-content {
  width: 100%;
  padding: 1rem;
  position: relative;
  z-index: 1;
}

.files-mosaic {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 0.75rem;
}

.file-tile {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 0.85rem 0.75rem;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: all 0.15s ease-in-out;
  cursor: default;
  min-height: 150px;
  overflow: hidden;
}

.file-tile:hover {
  border-color: #0d6efd;
  box-shadow: 0 2px 8px rgba(13, 110, 253, 0.12);
  transform: translateY(-2px);
}

.file-tile.is-previewable {
  cursor: pointer;
}

.context-menu-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1060;
}

.file-context-menu {
  position: fixed;
  z-index: 1070;
  min-width: 200px;
  background-color: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0.375rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  padding: 0.35rem 0;
  margin: 0;
  font-size: 0.9rem;
}

.file-context-menu-header {
  display: flex;
  align-items: center;
  padding: 0.4rem 0.85rem;
  font-size: 0.8rem;
  color: #6c757d;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 0.25rem;
  max-width: 240px;
}

.file-context-menu-header i {
  flex-shrink: 0;
}

.file-context-menu-header span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-context-menu-item {
  display: flex;
  align-items: center;
  padding: 0.45rem 0.85rem;
  cursor: pointer;
  color: #212529;
  transition: background-color 0.1s ease-in-out;
  user-select: none;
}

.file-context-menu-item:hover:not(.disabled) {
  background-color: #f1f3f5;
}

.file-context-menu-item:active:not(.disabled) {
  background-color: #e9ecef;
}

.file-context-menu-item.disabled {
  color: #adb5bd;
  cursor: not-allowed;
}

.file-context-menu-item i {
  width: 1.1rem;
  text-align: center;
}

.file-tile.is-folder {
  background-color: #fffbf2;
}

.file-tile-icon {
  margin-bottom: 0.5rem;
}

.file-tile.is-folder .file-tile-icon i {
  color: #f0ad4e;
}

.file-tile:not(.is-folder) .file-tile-icon i {
  color: #0d6efd;
}

.file-tile-name {
  font-size: 0.85rem;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.2;
  margin-bottom: 0.25rem;
  width: 100%;
}

.file-tile-meta {
  margin-bottom: 0.15rem;
  font-size: 0.75rem;
}

.file-tile-date {
  font-size: 0.7rem;
}

.spreadsheet-preview {
  background-color: #ffffff;
  overflow-x: auto;
}

.spreadsheet-preview :deep(.spreadsheet-sheet-title) {
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
}

.spreadsheet-preview :deep(table) {
  border-collapse: collapse;
  font-size: 0.85rem;
  min-width: 100%;
}

.spreadsheet-preview :deep(table td),
.spreadsheet-preview :deep(table th) {
  border: 1px solid #dee2e6;
  padding: 0.35rem 0.6rem;
  white-space: nowrap;
}

.spreadsheet-preview :deep(table tr:nth-child(even)) {
  background-color: #f8f9fa;
}

.document-preview {
  background-color: #ffffff;
  max-width: 900px;
  margin: 0 auto;
  font-size: 0.95rem;
  line-height: 1.6;
}

.document-preview :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin-bottom: 1rem;
}

.document-preview :deep(table td),
.document-preview :deep(table th) {
  border: 1px solid #dee2e6;
  padding: 0.4rem 0.6rem;
}

.document-preview :deep(img) {
  max-width: 100%;
}
</style>
